// src/components/AlocarCadeteExternoModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { formatarData, getNormalizedDateString } from '../utils/helpers';
import { db, paths } from '../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Limpa sujeiras, letras e símbolos do banco de dados (ex: "3º" vira 3)
const limparNumero = (val) => {
    if (val === undefined || val === null || val === '') return '';
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        const apenasNumeros = val.replace(/\D/g, '');
        if (apenasNumeros !== '') return parseInt(apenasNumeros, 10);
    }
    return '';
};

// Limpa patentes para organizar a ordem alfabética perfeitamente
const limparNomeParaSort = (nome) => {
    if (!nome) return '';
    return nome.replace(/^(Al Of PM|Cad PM|Cadete|Aluno Oficial|Escalante)\s+/i, '').trim().toUpperCase();
};

const extrairNumero = (c, escolhasAnteriores = []) => {
    // 1. Busca profunda no perfil
    const chavesParaBuscar = [
        'ordem_antiguidade', 'ordemAntiguidade', 'ordem', 'Ordem', 'ORDEM',
        'numero', 'Numero', 'NUMERO', 'numero_chamada', 'numeroChamada',
        'classificacao', 'Classificacao', 'antiguidade', 'Antiguidade'
    ];

    for (let chave of chavesParaBuscar) {
        const numLimpo = limparNumero(c[chave]);
        if (numLimpo !== '') return numLimpo;
    }
    
    // 2. Memória do histórico (Procura até pelo nome se o ID não bater)
    if (escolhasAnteriores && escolhasAnteriores.length > 0) {
        const historico = escolhasAnteriores.find(e => 
            e.tipo === 'EXTERNO' && 
            (e.cadete_id === c.id || e.usuario_id === c.id || e.id_cadete === c.id ||
            (e.cadete_nome && c.nome && e.cadete_nome.toUpperCase().includes(c.nome.split(' ')[0].toUpperCase())))
        );
        if (historico) {
            const histLimpo = limparNumero(historico.ordem_externa || historico.ordem_antiguidade);
            if (histLimpo !== '') return histLimpo;
        }
    }

    // 3. MÁGICA: Cálculo Automático por Ordem Alfabética (Número de Chamada)
    if (c.numero_calculado) return c.numero_calculado;

    return ''; 
};

const extrairRE = (c) => {
    const chavesRE = ['matricula', 're', 'RE', 'registro', 'numero_policial'];
    for (let chave of chavesRE) {
        if (c[chave] && String(c[chave]).trim() !== '') return String(c[chave]).trim();
    }
    return '';
};

export const AlocarCadeteExternoModal = ({ show, onClose, onConfirm, stages, processing, pelotoes = [], todasEscolhas = [] }) => {
    const [selectedPelotaoId, setSelectedPelotaoId] = useState('');
    const [cadetesDoPelotao, setCadetesDoPelotao] = useState([]);
    const [selectedCadeteId, setSelectedCadeteId] = useState('');
    const [isLoadingCadetes, setIsLoadingCadetes] = useState(false);

    const [formData, setFormData] = useState({
        nome: '',
        re: '',
        pelotao_origem: '',
        ordem_antiguidade: '',
        data_estagio_id: ''
    });
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show) {
            setSelectedPelotaoId('');
            setCadetesDoPelotao([]);
            setSelectedCadeteId('');
            setFormData({ nome: '', re: '', pelotao_origem: '', ordem_antiguidade: '', data_estagio_id: '' });
            setError('');
        }
    }, [show]);

    const handlePelotaoChange = async (e) => {
        const pelotaoId = e.target.value;
        setSelectedPelotaoId(pelotaoId);
        
        setSelectedCadeteId('');
        setCadetesDoPelotao([]);
        setFormData(prev => ({ ...prev, nome: '', re: '', pelotao_origem: '', ordem_antiguidade: '' }));

        if (!pelotaoId) return;

        setIsLoadingCadetes(true);
        try {
            const q = query(
                collection(db, paths.users), 
                where("pelotao_id", "==", pelotaoId),
                where("nivel", "in", ["cadete", "escalante", "cadete_academico"])
            );
            const snapshot = await getDocs(q);
            const rawCadetes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // TÁTICA PMESP: Ordena alfabeticamente para gerar um "Número de Chamada" reserva
            const listaAlfabetica = [...rawCadetes].sort((a, b) => limparNomeParaSort(a.nome).localeCompare(limparNomeParaSort(b.nome)));
            
            const listaCadetesComCalculo = rawCadetes.map(c => {
                const index = listaAlfabetica.findIndex(x => x.id === c.id);
                return { ...c, numero_calculado: index + 1 };
            });

            listaCadetesComCalculo.sort((a, b) => {
                const numA = extrairNumero(a, todasEscolhas);
                const numB = extrairNumero(b, todasEscolhas);
                const valA = numA === '' ? 999 : Number(numA);
                const valB = numB === '' ? 999 : Number(numB);
                return valA - valB;
            });
            
            setCadetesDoPelotao(listaCadetesComCalculo);
        } catch (err) {
            console.error("Erro ao buscar cadetes:", err);
            setError("Falha ao carregar cadetes do pelotão selecionado.");
        } finally {
            setIsLoadingCadetes(false);
        }
    };

    const handleCadeteChange = (e) => {
        const cadeteId = e.target.value;
        setSelectedCadeteId(cadeteId);

        const cadete = cadetesDoPelotao.find(c => c.id === cadeteId);
        if (cadete) {
            const antiguidade = extrairNumero(cadete, todasEscolhas);
            const reEncontrado = extrairRE(cadete);
            
            setFormData(prev => ({
                ...prev,
                nome: cadete.nome || '',
                re: reEncontrado || '',
                pelotao_origem: cadete.pelotao_nome || cadete.pelotao || 'N/D',
                ordem_antiguidade: antiguidade 
            }));
        } else {
            setFormData(prev => ({ ...prev, nome: '', re: '', pelotao_origem: '', ordem_antiguidade: '' }));
        }
    };

    const handleEstagioChange = (e) => {
        setFormData(prev => ({ ...prev, data_estagio_id: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        const missing = [];
        if (!formData.nome) missing.push("Nome do Cadete");
        if (!formData.pelotao_origem) missing.push("Pelotão de Origem");
        if (formData.ordem_antiguidade === '' || formData.ordem_antiguidade === undefined) missing.push("Antiguidade");
        if (!formData.data_estagio_id) missing.push("Vaga do Estágio");

        if (missing.length > 0) {
            setError(`Faltando dados obrigatórios: ${missing.join(', ')}. Por favor, preencha manualmente se necessário.`);
            return;
        }

        const selectedStage = stages.find(s => s.id === formData.data_estagio_id);
        if (!selectedStage) {
            setError("Estágio inválido.");
            return;
        }

        const escolhasDoCadete = todasEscolhas.filter(escolha => 
            escolha.cadete_id === selectedCadeteId || 
            escolha.usuario_id === selectedCadeteId ||
            escolha.user_id === selectedCadeteId ||
            escolha.id_cadete === selectedCadeteId ||
            (escolha.cadete_nome && formData.nome && escolha.cadete_nome.toUpperCase() === formData.nome.toUpperCase())
        );

        const jaEstagiouNoLocal = escolhasDoCadete.some(escolha => {
            if (escolha.opcao_impossibilidade) return false;
            const estagioDaEscolha = stages.find(s => s.id === escolha.data_estagio_id);
            const nomeDoLocalDaEscolha = escolha.local_nome || (estagioDaEscolha ? estagioDaEscolha.local_nome : null);
            return nomeDoLocalDaEscolha === selectedStage.local_nome;
        });

        if (jaEstagiouNoLocal) {
            setError(`Operação bloqueada: Este cadete já realizou ou está escalado para um estágio no batalhão/local "${selectedStage.local_nome}". Por favor, selecione uma vaga diferente.`);
            return;
        }

        onConfirm({
            ...formData,
            id: selectedCadeteId,
            stageData: selectedStage
        });
    };

    const availableStages = useMemo(() => {
        const hojeStr = getNormalizedDateString(new Date());

        const ocupacaoMap = todasEscolhas.reduce((acc, escolha) => {
            if (!escolha.opcao_impossibilidade && escolha.data_estagio_id) {
                acc[escolha.data_estagio_id] = (acc[escolha.data_estagio_id] || 0) + 1;
            }
            return acc;
        }, {});

        return stages.filter(s => {
            const isFuture = getNormalizedDateString(s.data) >= hojeStr;
            if (!isFuture) return false;

            const ocupadas = ocupacaoMap[s.id] || 0;
            const capacidadeTotal = s.capacidade || 1;

            return ocupadas < capacidadeTotal;
        }).sort((a, b) => a.data.toDate() - b.data.toDate()); 

    }, [stages, todasEscolhas]);

    return (
        <Modal show={show} onClose={onClose} title="Alocar Cadete de Outro Pelotão" size="md">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="p-3 bg-blue-50 text-blue-800 text-xs rounded border border-blue-200 mb-4">
                    <p className="font-bold">Modo de Pesquisa:</p>
                    <ul className="list-disc pl-4 mt-1 space-y-1">
                        <li>Selecione o <strong>Pelotão de origem</strong> e o <strong>Cadete</strong>.</li>
                        <li>Selecione uma <strong>Vaga Disponível</strong> (vagas cheias são ocultadas).</li>
                    </ul>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">1. Selecione o Pelotão de Origem</label>
                    <select 
                        value={selectedPelotaoId} 
                        onChange={handlePelotaoChange} 
                        className="w-full border p-2 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                        <option value="">-- Selecione o Pelotão --</option>
                        {pelotoes
                            .filter(p => p.nome && p.nome.includes("4º CFO"))
                            .map(p => (
                                <option key={p.id} value={p.id}>{p.nome}</option>
                            ))
                        }
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        2. Selecione o Cadete 
                        {isLoadingCadetes && <span className="text-xs text-blue-500 ml-2">(Carregando...)</span>}
                    </label>
                    <select 
                        value={selectedCadeteId} 
                        onChange={handleCadeteChange} 
                        disabled={!selectedPelotaoId || isLoadingCadetes}
                        className="w-full border p-2 rounded bg-white focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100"
                    >
                        <option value="">
                            {cadetesDoPelotao.length === 0 && selectedPelotaoId ? "(Nenhum cadete encontrado)" : "-- Selecione o Cadete --"}
                        </option>
                        {cadetesDoPelotao.map(c => {
                            const num = extrairNumero(c, todasEscolhas);
                            const re = extrairRE(c);
                            return (
                                <option key={c.id} value={c.id}>
                                    {num !== '' ? `${num}º` : 'S/N'} - {c.nome} {re ? `(RE: ${re})` : ''}
                                </option>
                            );
                        })}
                    </select>
                </div>

                {selectedCadeteId && (
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded border items-center">
                        <div className="col-span-2 grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 font-bold mb-1">Nome</p>
                                <p className="text-sm text-gray-800 font-medium truncate">{formData.nome}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 font-bold mb-1">RE (Matrícula)</p>
                                <input 
                                    type="text" 
                                    value={formData.re} 
                                    onChange={(e) => setFormData(prev => ({ ...prev, re: e.target.value }))}
                                    className={`w-full text-sm border p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.re ? 'border-gray-300 bg-white' : 'border-yellow-400 bg-yellow-50'}`}
                                    placeholder="Ex: 123456-7"
                                />
                            </div>
                        </div>
                        <div className="col-span-2">
                            <p className="text-xs text-gray-500 font-bold mb-1">Antiguidade (Nº da Chamada)</p>
                            <input 
                                type="number" 
                                value={formData.ordem_antiguidade} 
                                onChange={(e) => setFormData(prev => ({ ...prev, ordem_antiguidade: e.target.value }))}
                                className={`w-full text-sm border p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.ordem_antiguidade ? 'border-gray-300 bg-white' : 'border-red-400 bg-red-50'}`}
                                placeholder="Insira o Nº de Antiguidade"
                            />
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700">3. Escolher Vaga Disponível</label>
                    <select 
                        name="data_estagio_id" 
                        value={formData.data_estagio_id} 
                        onChange={handleEstagioChange} 
                        disabled={!selectedCadeteId}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100"
                        required
                    >
                        <option value="">
                            {availableStages.length === 0 ? "(Nenhuma vaga disponível)" : "Selecione uma data/local..."}
                        </option>
                        {availableStages.map(s => (
                            <option key={s.id} value={s.id}>
                                {formatarData(s.data)} - {s.local_nome} ({s.tipo}/{s.turno})
                            </option>
                        ))}
                    </select>
                </div>

                {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded border border-red-200">{error}</p>}

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button onClick={onClose} variant="secondary" disabled={processing}>Cancelar</Button>
                    <Button type="submit" variant="primary" disabled={processing || !selectedCadeteId || !formData.data_estagio_id}>
                        {processing ? "Alocando..." : "Confirmar Alocação"}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};